/**
 * Touch gesture classification. Gestures only fire after a deliberate
 * horizontal drag so vertical scrolling and taps keep working; the same
 * actions are always available as visible buttons.
 */
export type SwipeDirection = 'left' | 'right';

export interface SwipeOptions {
  /** Minimum horizontal travel in CSS pixels. */
  threshold?: number;
  /** Horizontal travel must exceed vertical travel by this factor. */
  dominance?: number;
}

export function classifySwipe(dx: number, dy: number, options: SwipeOptions = {}): SwipeDirection | null {
  const threshold = options.threshold ?? 56;
  const dominance = options.dominance ?? 1.4;
  if (Math.abs(dx) < threshold) return null;
  if (Math.abs(dx) < Math.abs(dy) * dominance) return null;
  return dx < 0 ? 'left' : 'right';
}

export function classifySwipePoints(
  start: { x: number; y: number },
  end: { x: number; y: number },
  options?: SwipeOptions,
): SwipeDirection | null {
  return classifySwipe(end.x - start.x, end.y - start.y, options);
}

/** Right swipe returns to the chat list, matching the visible back button. */
export function isBackSwipe(dx: number, dy: number, options?: SwipeOptions): boolean {
  return classifySwipe(dx, dy, options) === 'right';
}

/** Left swipe on a message starts a reply, matching the visible reply button. */
export function isReplySwipe(dx: number, dy: number, options?: SwipeOptions): boolean {
  return classifySwipe(dx, dy, options) === 'left';
}
